import type { CSSProperties } from "react";
import { withAlpha, type ThemePalette } from "../lib/themes";

/** A tiny mock of the app shell painted in one theme's palette: a canvas, a
 *  sidebar rail with an accent square and two muted lines, a panel with a dot
 *  and a line of text, and a row with an accent block beside a panel block.
 *
 *  A web port of WakiThemeMiniature from the Waki Mac apps, so the picker reads
 *  the same everywhere. It is drawn from the same palette object that generates
 *  the theme's CSS (lib/themes.ts), so the preview cannot drift from what the
 *  app paints. The caller passes the palette for the current mode. */
export function ThemeMiniature({ palette }: { palette: ThemePalette }) {
  const p = palette;
  const bar = (width: number, height: number, background: string, radius = 2): CSSProperties => ({
    width,
    height,
    background,
    borderRadius: radius,
    flexShrink: 0,
  });
  return (
    <div
      aria-hidden="true"
      style={{
        width: 84,
        height: 54,
        flexShrink: 0,
        display: "flex",
        gap: 5,
        padding: 6,
        boxSizing: "border-box",
        borderRadius: 8,
        overflow: "hidden",
        border: `1px solid ${p.border}`,
        background: `linear-gradient(135deg, ${p.bg1}, ${p.bg2})`,
      }}
    >
      {/* Sidebar rail */}
      <div
        style={{
          width: 18,
          flexShrink: 0,
          borderRadius: 4,
          background: p.bar,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          padding: "6px 0",
        }}
      >
        <span style={bar(8, 8, p.accent)} />
        <span style={bar(10, 3, mute(p.muted, 0.5))} />
        <span style={bar(10, 3, mute(p.muted, 0.35))} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            flex: 1,
            borderRadius: 3,
            background: p.panel2,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: 5,
          }}
        >
          <span style={bar(5, 5, p.accent2, 9999)} />
          <span style={bar(22, 3, mute(p.text, 0.45))} />
        </div>
        <div style={{ height: 10, display: "flex", gap: 4 }}>
          <span style={{ flex: 1, borderRadius: 3, background: p.accent }} />
          <span style={{ flex: 1, borderRadius: 3, background: p.panel2 }} />
        </div>
      </div>
    </div>
  );
}

/** Palette text colours are hex; the preview wants them faded. */
function mute(hex: string, alpha: number): string {
  return hex.startsWith("#") ? withAlpha(hex, alpha) : hex;
}
